import React, { useContext, useState } from "react"
import { Text, View } from "react-native"
import { Formik } from "formik"
import * as yup from 'yup'
import { ThemedTextInput } from "../input/textinputs/themed-text-input"
import { ThemedButton } from "../input/buttons"
import { ThemeContext } from "../../utility/context/ThemeContext"
import { textStyles } from "../../style"
import StarRating from "../input/misc/star-rating"
import { CustomActionSheetScrollView } from "../CustomActionSheet"
import { log } from "../pwa"
import Analytics from "../analytics"
import api from "../../api-client"
import SuccessAnimation from "../success-animation"
import ErrorAnimation from "../error-animation"

type Props = {
  hideSheet?: () => void
}

enum SubmitStep {
  NotSubmitted,
  Submitting,
  Submitted,
  InputError,
  ServerError
}

const AppFeedbackForm: React.FC<Props> = ({ hideSheet }: Props): React.ReactElement => {
  const theme = useContext(ThemeContext)
  const { textMain } = theme.theme.colors

  const [submitStep, setSubmitStep] = useState<SubmitStep>(SubmitStep.NotSubmitted)
  const [serverErrors, setServerErrors] = useState<string[]>([])

  type FormValues = { rating: number; comment: string }

  const validationSchema = yup.object().shape({
    rating: yup.number().required().min(1, 'Please select a rating').max(5).label('Rating'),
    comment: yup.string().optional(),
  })

  if (submitStep === SubmitStep.Submitted) {
    return <>
    <CustomActionSheetScrollView>
      <View style={{ gap: 8 }}>
        <View style={{ alignItems: 'center' }}>
          <SuccessAnimation size={250} />
        </View>
        <Text style={[textStyles.h2, { color: textMain }]}>Thank you for your feedback!</Text>
        <Text style={[textStyles.body, { color: textMain }]}>
          We appreciate your feedback and will use it to improve our app
        </Text>
      </View>
      </CustomActionSheetScrollView>
      <View style={{ padding: 16 }}>
        <ThemedButton text="Close" onPress={hideSheet} variant="primary" />
      </View>
    </>
  }

  if (submitStep === SubmitStep.ServerError) {
    return <>
      <CustomActionSheetScrollView>
        <View style={{ gap: 8 }}>
          <View style={{ alignItems: 'center' }}>
            <ErrorAnimation size={250} />
          </View>
          <Text style={[textStyles.h2, { color: textMain }]}>Error submitting feedback</Text>
          <Text style={[textStyles.body, { color: textMain }]}>
            We apologize for the inconvenience. Please try again later.
          </Text>
        </View>
      </CustomActionSheetScrollView>
      <View style={{ padding: 16 }}>
        <ThemedButton text="Close" onPress={hideSheet} variant="primary" />
      </View>
    </>
  }

  return (
    <Formik<FormValues>
      initialValues={{ rating: 0, comment: "" }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        setSubmitStep(SubmitStep.Submitting)
        setServerErrors([])
        Analytics.track('feedback_submitted', {
          rating: values.rating,
          comment: values.comment,
        })
        api.submitAppFeedback({ rating: values.rating, comment: values.comment }).then(() => {
          setSubmitStep(SubmitStep.Submitted)
        }).catch((err) => {
          // Capture server validation errors vs generic server error
          if (err?.status === 400 && Array.isArray(err?.errors)) {
            setServerErrors(err.errors)
            setSubmitStep(SubmitStep.InputError)
          } else {
            log.error(new Error('Error submitting app feedback'), { tags: { message: 'error submitting app feedback' }, extra: { rating: values.rating, comment: values.comment, error: err } })
            setSubmitStep(SubmitStep.ServerError)
          }
        })
      }}
    >
      {({ handleChange, handleSubmit, values, errors, touched, setFieldValue }) => (
        <View style={{ flex: 1 }}>
          <CustomActionSheetScrollView>
            <View style={{ gap: 8 }}>
              <Text style={[textStyles.h2, { color: textMain }]}>Provide Feedback</Text>
              <Text style={[textStyles.body, { color: textMain }]}>
                Let us know how we&apos;re doing and what we can do to improve
              </Text>
            </View>
            <StarRating
              label="How would you rate your experience?"
              value={values.rating}
              onChange={(v) => setFieldValue('rating', v)}
              max={5}
              required
              errorText={touched.rating ? (errors.rating as string) : undefined}
              disabled={submitStep === SubmitStep.Submitting}
            />

            <ThemedTextInput
              label="Comment (Optional, max 1,000 characters)"
              value={values.comment}
              onChange={handleChange('comment')}
              placeholder="Tell us more about your experience"
              textInputProps={{
                multiline: true,
                maxLength: 1000,
                numberOfLines: 5,
                style: { height: 120, textAlignVertical: "top" },
                returnKeyType: "done",
              }}
              disabled={submitStep === SubmitStep.Submitting}
            />

            {submitStep === SubmitStep.InputError && serverErrors.length > 0 && (
              <View style={{ gap: 4 }}>
                {serverErrors.map((err, i) => (
                  <Text key={i} style={[textStyles.subscript, { color: 'red' }]}>{err}</Text>
                ))}
              </View>
            )}
          </CustomActionSheetScrollView>
          <View style={{ padding: 16 }}>
            <ThemedButton
              text="Submit"
              onPress={() => handleSubmit()}
              variant="primary"
              disabled={!values.rating || submitStep === SubmitStep.Submitting}
              loading={submitStep === SubmitStep.Submitting}
            />
          </View>
        </View>
      )}
    </Formik>
  )
}

export default AppFeedbackForm